<template>
  <div class="flex flex-col">
    <form
      v-if="hasQuestions"
      :id="QUESTIONS_FORM_ID"
      class="order-1 lg:order-2"
      @submit.prevent="onSubmit"
    >
      <QuestionsFormItem
        v-for="(question, index) in props.questions"
        :key="question.key"
        :class="index === 0 ? '' : 'mt-32'"
        :question="{
          ...question,
          value: localValues[question.key] ?? question.value,
        }"
        @answer="onAnswer"
      />

      <QuestionsFormActions
        :button-label="props.buttonLabel"
        :disabled="isSubmitDisabled"
        :form-id="QUESTIONS_FORM_ID"
        :has-submit-button="props.hasSubmitButton"
        :is-loading-offer="props.isLoadingOffer"
      />
    </form>

    <GradingwithAI
      v-if="props.showAiGrading"
      class="order-2 lg:order-1"
      :is-step-complete="props.isStepComplete"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import GradingwithAI from '@backmarket/nuxt-layer-buyback/components/GradingwithAI/GradingwithAI.vue'

import type { RenderQuestion } from '~/scopes/buyback/composables/useFunnelQuestions'

import QuestionsFormActions from './QuestionsFormActions.vue'
import QuestionsFormItem from './QuestionsFormItem.vue'
import { QUESTIONS_FORM_ID } from './constants'

export interface QuestionsFormProps {
  questions: Array<RenderQuestion>
  buttonLabel: string
  hasSubmitButton?: boolean
  isLoadingOffer?: boolean
  isStepComplete?: boolean
  showAiGrading?: boolean
}

const props = withDefaults(defineProps<QuestionsFormProps>(), {
  hasSubmitButton: true,
  isLoadingOffer: false,
  isStepComplete: false,
  showAiGrading: false,
})

const emit = defineEmits<{
  (event: 'answer', key: string, value: string | false): void
  (event: 'submit'): void
}>()

const hasQuestions = computed(() => props.questions.length > 0)

const localValues = ref<Record<string, string | false>>({})

const isSubmitDisabled = computed(() =>
  props.questions.some((q) => {
    const value = localValues.value[q.key] ?? q.value

    return value === false || value === ''
  }),
)

watch(
  () => props.questions,
  () => {
    localValues.value = {}
  },
)

function onAnswer(key: string, value: string | false) {
  if (props.hasSubmitButton) {
    localValues.value = { ...localValues.value, [key]: value }
  } else {
    emit('answer', key, value)
  }
}

function onSubmit() {
  Object.entries(localValues.value).forEach(([key, value]) => {
    emit('answer', key, value)
  })
  localValues.value = {}
  emit('submit')
}
</script>
